

















































































































































































































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState, mapGetters } from "vuex";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import ProvisioningMixin from "@/mixins/provisioningMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ActionTypes } from "@/data/enums";
import { getNewValues } from "@/helpers/forms";
import { ProvisionFieldTypes } from "@/data/enums/provisioning";

// Types
import {
  IProviderConfigurationForm,
  IProvisionConfiguration,
  IProvisionProvider,
  IProvisionCategoryAttribute,
  IBlueprintField
} from "@/models/provisioning";
import { IState } from "@/store";
import { LogoSize, LogoType } from "@/data/enums/logos";
import { IProductCategory } from "@/models/products";

@Component({
  components: {
    "provision-field": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/provisionField.vue"
      ),
    "manage-attributes-list": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/manageAttributeList.vue"
      )
  },
  computed: {
    ...mapGetters({
      providersScopeGetter: "data/provisioning/providers/scope",
      providerFieldsScopeGetter:
        "data/provisioning/providers/providerFieldsScope",
      categoryAttributesScopeGetter:
        "data/provisioning/categories/attributesScope"
    }),
    ...mapState({
      configurationScope(state: IState, getters) {
        return getters[
          `data/${this.configurationsDataModule}/configurationScope`
        ](this.configurationId);
      },
      configuration(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.configurationsDataModule,
          scope: this.configurationScope
        });
      },

      categoryAttributes(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesDataModule,
          scope: this.categoryAttributesScopeGetter(this.form.category_id)
        });
      },

      providers(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.providersDataModule,
          scope: this.providersScopeGetter(this.form.category_id)
        });
      },

      providerFields(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.providersDataModule,
          scope: this.providerFieldsScopeGetter(this.form.provider_id)
        });
      }
    })
  }
})
export default class ManageProviderConfigurationModal extends Mixins(
  RequestPromises,
  HasValidation,
  ProvisioningMixin
) {
  public declare $refs: {
    name: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Getters
  public providersScopeGetter!: Function;
  public providerFieldsScopeGetter!: Function;
  public categoryAttributesScopeGetter!: Function;

  // State
  public configuration!: IProvisionConfiguration;
  public configurationScope!: string;
  public providers!: IProvisionProvider[];
  public providerFields!: IBlueprintField[];
  public categoryAttributes!: IProvisionCategoryAttribute[];

  // Prop
  @Prop({ default: null })
  public configurationId!: IProvisionConfiguration["id"];

  @Prop({ default: null })
  public categoryId!: IProductCategory["id"];

  @Prop({ default: null })
  public providerId!: IProvisionProvider["id"];

  @Prop({ default: ActionTypes.CREATE })
  public actionType!: ActionTypes;

  // Variables

  public guardFunctionalities = ["get_provision_configuration"];

  public form = {
    name: "",
    category_id: null,
    provider_id: null,
    enabled: true,
    category_default: false,
    provider_default: false,
    field_values: {},
    attribute_values: {}
  } as IProviderConfigurationForm;
  public formClone = this.form as IProviderConfigurationForm;

  public categoriesDataModule = DataModules.PROVISIONING_CATEGORIES;
  public providersDataModule = DataModules.PROVISIONING_PROVIDERS;
  public configurationsDataModule = DataModules.PROVISIONING_CONFIGURATIONS;

  public provisionFieldTypes = ProvisionFieldTypes;

  public isLoading = false;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public providersLoading = false;
  public providerFieldsLoading = false;

  public created() {
    if (!this.isContentAvailable) {
      return;
    }

    if (this.categoryId) {
      this.form.category_id = this.categoryId;
    }

    if (this.providerId) {
      this.form.provider_id = this.providerId;
    }

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getCategoriesWithProviders();

      if (this.isUpdateModal) {
        await this.getConfiguration();

        this.form = this.$_.merge(
          this.form,
          this.$_.pick(this.configuration, [
            "name",
            "category_id",
            "provider_id",
            "field_values",
            "enabled",
            "category_default",
            "provider_default"
          ])
        );
      }

      if (this.form.category_id) {
        await Promise.all([this.getProviders(), this.getCategoryAttributes()]);
        this.fillAttributes();
      }

      if (this.form.provider_id) {
        await this.getProviderFields();
      }

      this.form = this.$_.cloneDeep(this.form);
      this.formClone = this.$_.cloneDeep(this.form);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;

    this.$nextTick(() => {
      if (!this.$refs.name) {
        return;
      }
      this.$refs.name.focus();
    });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProviders(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.providersDataModule}/list`, {
      categoryId: this.form.category_id,
      scope: this.providersScopeGetter(this.form.category_id),
      vm: this,
      ignoreStored
    });
  }

  public getConfiguration(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.configurationsDataModule}/get`, {
      id: this.configurationId,
      scope: this.configurationScope,
      vm: this,
      ignoreStored
    });
  }

  public getProviderFields(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${this.providersDataModule}/listProviderFields`,
      {
        categoryId: this.form.category_id,
        providerId: this.form.provider_id,
        scope: this.providerFieldsScopeGetter(this.form.provider_id),
        vm: this,
        ignoreStored
      }
    );
  }

  public getCategoryAttributes(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${this.categoriesDataModule}/getAttributes`,
      {
        id: this.form.category_id,
        scope: this.categoryAttributesScopeGetter(this.form.category_id),
        vm: this,
        ignoreStored
      }
    );
  }

  public fillAttributes() {
    return new Promise<void>(resolve => {
      this.$_.forEach(this.categoryAttributes, attribute => {
        if (attribute.is_array) {
          this.$set(this.form.attribute_values, attribute.name, []);

          const attributeValues = this.$_.filter(
            this.$_.get(this.configuration, "attribute_values", []),
            ["attribute_id", attribute.id]
          );
          this.$set(
            this.form.attribute_values,
            attribute.name,
            attributeValues.length
              ? this.$_.map(
                  attributeValues,
                  attributeValue => attributeValue.value
                )
              : [""]
          );
        } else {
          this.$set(
            this.form.attribute_values,
            attribute.name,
            this.$_.get(
              this.$_.find(
                this.$_.get(this.configuration, "attribute_values", []),
                ["attribute_id", attribute.id]
              ),
              "value",
              null
            )
          );
        }
      });
      resolve();
    });
  }

  public get isUpdateModal() {
    return this.actionType === ActionTypes.UPDATE;
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) {
      return;
    }

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      const result = this.isUpdateModal
        ? await this.updateConfiguration()
        : await this.createConfiguration();

      this.$buefy.toast.open({
        message: this.$t(`_.object_successfully_updated`, {
          object: this.$t("_.provider_configuration")
        }) as string
      });

      this.$bus.$emit("refreshConfigurationsTable");

      this.$emit("success", result);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }

    this.isProcessing = false;
  }

  public createConfiguration() {
    return this.$store.dispatch(
      `data/${this.configurationsDataModule}/create`,
      {
        data: this.form
      }
    );
  }

  public updateConfiguration() {
    return this.$store.dispatch(
      `data/${this.configurationsDataModule}/update`,
      {
        id: this.$_.get(this.configuration, "id"),
        data: this.newValues
      }
    );
  }

  public get buttonIsDisabled() {
    return (
      this.hasErrors ||
      this.isProcessing ||
      this.$_.isEqual(this.form, this.formClone) ||
      this.requestError
    );
  }

  public async reloadProviders() {
    try {
      this.providersLoading = true;
      await this.getProviders(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.providersLoading = false;
  }

  public async reloadProviderFields() {
    try {
      this.providerFieldsLoading = true;
      await this.getProviderFields(true);
      await this.getCategoryAttributes(true);
      await this.fillAttributes();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.providerFieldsLoading = false;
  }

  public get selectedCategoryName() {
    return this.$_.get(
      this.$_.find(this.categories, ["id", this.form.category_id]),
      "name",
      ""
    );
  }

  public get selectedProviderName() {
    return this.$_.get(
      this.$_.find(this.providers, ["id", this.form.provider_id]),
      "name",
      ""
    );
  }

  public get newValues() {
    if (this.$_.isEqual(this.form, this.formClone)) {
      return {};
    }

    const results = getNewValues(this.form, this.formClone);
    delete results.field_values;
    delete results.attribute_values;

    const fieldValuesResults = getNewValues(
      this.form.field_values,
      this.formClone.field_values
    );

    const attributeValuesResults = getNewValues(
      this.form.attribute_values,
      this.formClone.attribute_values
    );

    return this.$_.merge(
      results,
      this.$_.isEmpty(fieldValuesResults)
        ? {}
        : { field_values: fieldValuesResults },
      this.$_.isEmpty(attributeValuesResults)
        ? {}
        : { attribute_values: attributeValuesResults }
    );
  }

  @Watch("form.category_id")
  public onCategoryChange(newCategoryId, oldCategoryId) {
    if (this.isLoading || this.isReloading) {
      return;
    }

    this.form.provider_id = null;
    this.clearProviderStore(this.providersScopeGetter(oldCategoryId));
    this.clearCategoryAttributesStore(
      this.categoryAttributesScopeGetter(oldCategoryId)
    );

    if (this.form.category_id === null) {
      return;
    }

    this.reloadProviders();
  }

  @Watch("form.provider_id")
  public onProviderChange(newProviderId, oldProviderId) {
    if (this.isLoading || this.isReloading) {
      return;
    }

    this.form.field_values = {};
    this.clearProviderFieldsStore(
      this.providerFieldsScopeGetter(oldProviderId)
    );

    if (this.form.category_id === null || this.form.provider_id === null) {
      return;
    }

    this.reloadProviderFields();
  }

  public get i18n() {
    return {
      title: this.isUpdateModal
        ? this.$t("_action.configure_object", {
            object: this.$t("_.provision_configuration")
              .toString()
              .toLowerCase()
          })
        : this.$t("_action.create_object", {
            object: this.$t("_.provision_configuration")
              .toString()
              .toLowerCase()
          })
    };
  }

  public get canSave() {
    return this.isUpdateModal
      ? this.$userCan("update_provision_configuration")
      : this.$userCan("create_provision_configuration");
  }

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public clearProviderStore(providersScope: string) {
    this.$store.dispatch("data/binList", {
      storeModule: this.providersDataModule,
      scope: providersScope,
      vm: this
    });
  }

  public clearProviderFieldsStore(providerFieldsScope: string) {
    this.$store.dispatch("data/binList", {
      storeModule: this.providersDataModule,
      scope: providerFieldsScope,
      vm: this
    });
  }

  public clearCategoryAttributesStore(categoryAttributesScope: string) {
    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesDataModule,
      scope: categoryAttributesScope,
      vm: this
    });
  }

  public getProviderLogoUrl(provider: IProvisionProvider) {
    return this.$_.get(
      this.$_.find(provider.logos || [], logo => {
        if (logo.type === LogoType.ICON && logo.size === LogoSize.ONE_X)
          return true;
        return false;
      }),
      "url",
      null
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: this.configurationsDataModule,
      scope: this.configurationScope,
      vm: this
    });

    this.clearProviderStore(this.providersScopeGetter(this.form.category_id));
    this.clearProviderFieldsStore(
      this.providerFieldsScopeGetter(this.form.provider_id)
    );
    this.clearCategoryAttributesStore(
      this.categoryAttributesScopeGetter(this.form.category_id)
    );
  }
}
